import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { PaginationPageSelect } from './PaginationPageSelect.client'

type PaginationProps = UiComponentProps<{
  totalPages: number
  currentPage: number
  nextPage: () => void
  previousPage: () => void
  goToPage: (page: number) => void
  seoLinks?: Array<{ page: number; url: string }>
  children?: React.ReactNode
}>

export function Pagination({
  totalPages,
  currentPage,
  goToPage,
  nextPage,
  previousPage,
  children,
  seoLinks,
  className,
  id,
  'data-testid': dataTestId = 'pagination',
  'aria-label': ariaLabel,
}: PaginationProps) {
  const { t } = useTranslation(['pagination', 'generic'])

  return (
    <>
      {children}
      {seoLinks && seoLinks.length > 0 && (
        <div className="sr-only">
          {seoLinks.map((link) => (
            <a key={link.page} href={link.url}>
              {t('pagination:pageDetails', { currentPage: link.page, totalPages })}
            </a>
          ))}
        </div>
      )}
      {totalPages > 1 && (
        <nav
          id={id}
          data-testid={dataTestId}
          aria-label={ariaLabel ?? t('pagination:ariaLabels.nav')}
          className={twMerge(className, 'flex w-full items-stretch justify-center gap-1 py-4')}
        >
          <Button
            variant="primary"
            size="lg"
            iconLeft={<Icon.ArrowLeft />}
            isRound
            aria-label={t('pagination:ariaLabels.previous')}
            onPress={() => {
              pushToGtmDataLayer({
                event: 'gaEvent',
                eventCategory: 'pagination',
                eventAction: 'clic',
                eventLabel: 'précédent',
              })
              previousPage()
            }}
            isDisabled={currentPage === 1}
          >
            {t('generic:previous')}
          </Button>
          <PaginationPageSelect
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => {
              pushToGtmDataLayer({
                event: 'gaEvent',
                eventCategory: 'pagination',
                eventAction: 'menu déroulant',
                eventLabel: String(page),
              })
              goToPage(page)
            }}
          />
          <Button
            variant="primary"
            size="lg"
            iconLeft={<Icon.ArrowRight />}
            isRound
            aria-label={t('pagination:ariaLabels.next')}
            onPress={() => {
              pushToGtmDataLayer({
                event: 'gaEvent',
                eventCategory: 'pagination',
                eventAction: 'clic',
                eventLabel: 'suivant',
              })
              nextPage()
            }}
            isDisabled={currentPage === totalPages}
          >
            {t('generic:next')}
          </Button>
        </nav>
      )}
    </>
  )
}
